import { memo, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { NivoBarChart } from '@trinity/chat-components/components/Chat.Charts/Bar';
import { NivoDoughnutChart } from '@trinity/chat-components/components/Chat.Charts/Doughnut';
import { ChartLegend } from '@trinity/chat-components/components/Chat.Charts/Legend';
import { NivoLineChart } from '@trinity/chat-components/components/Chat.Charts/Line';
import { NivoPieChart } from '@trinity/chat-components/components/Chat.Charts/Pie';
import { NivoStackedBarChart } from '@trinity/chat-components/components/Chat.Charts/StackedBar';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context';
import { BrandThemingContext } from '@trinity/chat-components/containers/Branding/Context';
import { BrandColorCategory, ColorPaletteType } from '@trinity/chat';
import type { Chart as IChart, LineChart, BarChart, PieChart, Line } from '@trinity/chat';

type Props = {
  item: IChart['chart'];
  type: IChart['type'];
};

export const Chart = memo((props: Props) => {
  const { colors, ready } = useChartColors();

  const legend = [];

  const renderChart = (chartType: string) => {
    switch (chartType) {
      case 'stacked-bar': {
        const chart = props.item as BarChart;
        for (let i = 0; i < chart.data.length; i++) {
          legend.push({ key: chart.data[i].x, color: colors[i] });
        }
        return <NivoStackedBarChart chart={chart} colors={colors} />;
      }

      case 'bar': {
        const chart = props.item as BarChart;
        for (let i = 0; i < chart.data.length; i++) {
          legend.push({ key: chart.data[i].x, color: colors[i] });
        }
        return <NivoBarChart chart={chart} colors={colors} />;
      }

      case 'pie': {
        const chart = props.item as PieChart;
        for (let i = 0; i < chart.data.length; i++) {
          legend.push({ key: chart.data[i].label, color: colors[i] });
        }
        return <NivoPieChart chart={chart} colors={colors} />;
      }
      case 'line': {
        const chart = props.item as LineChart;

        if (Array.isArray(chart.data)) {
          for (let i = 0; i < chart.data.length; i++) {
            const line = chart.data[i];
            legend.push({ key: line.label ?? line.id, color: colors[i] });
          }
        } else {
          const d = chart.data as Line;
          legend.push({ key: d.label ?? d.id, color: colors[0] });
        }
        return <NivoLineChart chart={chart} colors={colors} />;
      }

      case 'doughnut': {
        const chart = props.item as PieChart;
        for (let i = 0; i < chart.data.length; i++) {
          legend.push({ key: chart.data[i].label, color: colors[i] });
        }
        return <NivoDoughnutChart chart={chart} colors={colors} />;
      }

      default:
        return null;
    }
  };

  return (
    <div>
      <ChartRoot>
        {(ready && props.item) &&
          <>
            <Title>{props.item.title}</Title>
            {renderChart(props.type)}
            {legend.length > 0 && <ChartLegend legend={legend} />}
          </>}
      </ChartRoot>
    </div>
  );
});

const ChartRoot = styled.div`
  border: 1px solid var(--gray-l);
  border-radius: 15px;
  padding: 12px;
  margin-top: 10px;
  background-color: white;
`;

const Title = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
}));

const useChartColors = () => {
  const branding = useContext(BrandThemingContext);
  const theming = useContext(AppThemingContext);

  const ready = useMemo(() => {
    return !branding.query.isLoading &&
      branding.query.isFetchedAfterMount &&
      theming.query.isFetchedAfterMount &&
      theming.query.isFetchedAfterMount;
  }, [
    branding.query.isFetchedAfterMount,
    branding.query.isLoading,
    theming.query.isFetchedAfterMount,
  ]);

  const colors = useMemo(() => {
    if (theming.query.data?.enabled) {
      const paletteId = branding.query.data.theming.themes.find(x => x.categoryId === BrandColorCategory.Options)?.paletteId;
      return branding.query.data.theming.palettes.find(x => x.id === paletteId)?.data?.colors;
    }

    return branding.query.data.theming.palettes.find(x => x.typeId === ColorPaletteType.Preset && x.categoryId === BrandColorCategory.Options)?.data?.colors;
  }, [theming.query.data?.enabled, branding.query.data.theming.palettes, branding.query.data.theming.themes]);

  return {
    ready,
    colors,
  };
};